/**
 * Bloomreach subscriber for the generic tracking emitter.
 *
 * Maps vendor-agnostic business events to Bloomreach SDK calls.
 * Add new event mappings here as tracking requirements are defined.
 *
 * IMPORTANT: This module subscribes eagerly at import time (bottom of file).
 * This is intentional. SSR-hydrated components (lister, PDP) emit events
 * before the Bootstrap component mounts and calls initBloomreach().
 * Without eager subscription, those events would be lost on the generic emitter.
 * Events emitted before init are queued in bloomreachClient and flushed once
 * the SDK is initialized.
 */
import { emitter, EmitterEvent } from '../emitter';
import { anonymizeBloomreach, trackBloomreach } from './bloomreachClient';
import { BloomreachEventNames } from './constants';
// Partial, not a full Record — Bloomreach only needs to handle a subset of the
// vendor-agnostic events; other vendors (e.g. Fredhopper) may define events it
// doesn't care about.
const EVENT_MAP = {
    [EmitterEvent.VIEW_ITEM]: data => {
        trackBloomreach(BloomreachEventNames.VIEW_ITEM, data);
    },
    [EmitterEvent.CATEGORY_VIEW]: data => {
        trackBloomreach(BloomreachEventNames.CATEGORY_VIEW, data);
    },
    [EmitterEvent.CART_UPDATE]: data => {
        trackBloomreach(BloomreachEventNames.CART_UPDATE, data);
    },
    [EmitterEvent.LOGOUT]: () => {
        anonymizeBloomreach();
    },
};
let subscribed = false;
/** Subscribes to the generic tracking emitter. */
export const subscribeBloomreachToTracking = () => {
    if (subscribed) {
        return;
    }
    subscribed = true;
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.on(event, handler);
    });
};
// Subscribe eagerly so SSR-hydrated components (lister, PDP) can emit events
// before Bootstrap mounts. Events are queued in bloomreachClient until init.
subscribeBloomreachToTracking();
/** Unsubscribes from the generic tracking emitter. */
export const unsubscribeBloomreachFromTracking = () => {
    Object.entries(EVENT_MAP).forEach(([event, handler]) => {
        emitter.off(event, handler);
    });
    subscribed = false;
};
